import { clsx } from 'clsx';
import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type Ref,
  type RefObject,
} from 'react';
import { useIntl } from 'react-intl';

import * as styles from './DetailsLayout.sty.js';
import { DetailsLayoutContext } from './DetailsLayoutContext.js';
import { Overlay, OVERLAY_HANDLE_WIDTH } from './Overlay.js';
import {
  type ExtractedContentSlot,
  type ExtractedDetailsSlot,
} from './slots.js';
import { useLayoutState } from './useLayoutState.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useSafeLayoutEffect } from '../../../core/hooks/useSafeLayoutEffect.js';
import { Drawer } from '../../../overlays/drawer/Drawer.js';
import { clampPanelWidth } from '../../split-layout/split-sizing-utils.js';
import { SplitLayout } from '../../split-layout/SplitLayout.js';
import { convertToPx } from '../../split-layout/width-utils.js';
import {
  PageLayoutPortalContext,
  type PageLayoutPortalContextValue,
} from '../shared/PageLayoutPortalContext.js';
import { useTeleport } from '../shared/useTeleport.js';

type TeleportPair = ReturnType<typeof useTeleport>;

// Mirrors the resize-handle width used by SplitLayout (useSplitSizing) so the
// width reported here matches what the split panel actually renders.
const SPLIT_HANDLE_WIDTH = 8;

function DetailsLayoutInner({
  details,
  content,
  containerRef,
  contentTeleport,
  panelTeleport,
}: {
  details: ExtractedDetailsSlot;
  content: ExtractedContentSlot;
  containerRef: RefObject<HTMLDivElement | null>;
  contentTeleport: TeleportPair;
  panelTeleport: TeleportPair;
}) {
  const intl = useIntl();
  const layout = useLayoutState({ details, containerRef });

  const [intendedWidth, setIntendedWidth] = useState<number>();

  const containerWidth = layout.containerWidth;
  const controlledWidth = details.config.width;

  const clampForMode = useCallback(
    (value: number) => {
      switch (layout.mode) {
        // Overlay floats over the content, so it reserves no content space; it is
        // bounded by `maxWidth` (when set) and always by the container edge.
        case 'overlay':
          return clampPanelWidth(value, containerWidth, {
            panelMin: details.config.minWidth,
            panelMax: details.config.maxWidth ?? Infinity,
            contentMin: 0,
            handleWidth: details.config.resizable ? OVERLAY_HANDLE_WIDTH : 0,
          });
        case 'split':
          return clampPanelWidth(value, containerWidth, {
            panelMin: details.config.minWidth,
            panelMax: details.config.maxWidth ?? Infinity,
            contentMin: content.config.minWidth,
            handleWidth: details.config.resizable ? SPLIT_HANDLE_WIDTH : 0,
          });
        default:
          return value;
      }
    },
    [
      layout.mode,
      containerWidth,
      details.config.minWidth,
      details.config.maxWidth,
      details.config.resizable,
      content.config.minWidth,
    ],
  );

  const baseWidth = (() => {
    if (containerWidth === 0) {
      return undefined;
    }
    if (controlledWidth !== undefined) {
      return convertToPx(controlledWidth, containerWidth);
    }
    return (
      intendedWidth ?? convertToPx(details.config.defaultWidth, containerWidth)
    );
  })();
  const panelWidth =
    baseWidth !== undefined ? clampForMode(baseWidth) : undefined;

  const commitWidth = useCallback(
    (value: number) => {
      if (controlledWidth === undefined) {
        setIntendedWidth(value);
      }
      details.config.onResize?.(value);
    },
    [controlledWidth, details.config],
  );

  const detailsAriaLabel = intl.formatMessage({
    defaultMessage: 'Details',
    id: 'o3A99RFWS+N7bJe8',
    description: 'Accessible label for the details panel.',
  });

  const contextValue = useMemo(
    () => ({
      collapsed: layout.collapsed,
      setCollapsed: layout.setCollapsed,
      preferredLayout: layout.preferredLayoutMode,
      setPreferredLayout: layout.setPreferredLayoutMode,
      mode: layout.mode,
    }),
    [
      layout.collapsed,
      layout.setCollapsed,
      layout.preferredLayoutMode,
      layout.setPreferredLayoutMode,
      layout.mode,
    ],
  );

  // Bridge: DetailsLayoutContext.Provider below only covers the split/overlay
  // DOM rendered by this component. PageLayout.Details children live in a
  // different React subtree (they're portaled), so the provider doesn't reach
  // them. Publish the value through the registry so PageLayoutDetails can
  // re-wrap its portaled children with a provider of the same value.
  const pageLayoutPortalContext =
    useContext<PageLayoutPortalContextValue | null>(PageLayoutPortalContext);
  useSafeLayoutEffect(() => {
    if (!pageLayoutPortalContext) {
      return;
    }
    pageLayoutPortalContext.registry.register(
      'detailsLayoutContext',
      contextValue,
    );
  }, [pageLayoutPortalContext, contextValue]);

  const contentClassName = clsx(styles.content, content.domProps.className);
  const contentNode = (
    <main ref={content.ref} {...content.domProps} className={contentClassName}>
      <contentTeleport.Target />
    </main>
  );

  // `main` must always render inside SplitLayout.Content — never per-mode — or it remounts and resets scroll position (APPDEV-18829).
  const isSplit = layout.mode === 'split';
  const isOverlay = layout.mode === 'overlay';
  const isDrawer = layout.mode === 'drawer';
  // Render a hidden panel Target while the overlay container is unmeasured
  // so the teleport host stays attached across the measure frame.
  const isMeasuringOverlay = isOverlay && layout.containerWidth === 0;
  // Split-only: the panel's padding would floor the border-box width of the
  // 0-width column and reserve space in every other mode (APPDEV-19351).
  const panelClassName = isSplit
    ? clsx(styles.panel, details.domProps.className)
    : undefined;

  return (
    <DetailsLayoutContext.Provider value={contextValue}>
      <panelTeleport.Source>{details.children}</panelTeleport.Source>
      <SplitLayout>
        <SplitLayout.Content minWidth={content.config.minWidth}>
          {contentNode}
        </SplitLayout.Content>
        <SplitLayout.Panel
          {...(isSplit ? details.domProps : {})}
          ref={isSplit ? (details.ref as Ref<HTMLDivElement>) : undefined}
          minWidth={isSplit ? details.config.minWidth : 0}
          maxWidth={isSplit ? details.config.maxWidth : 0}
          defaultWidth={details.config.defaultWidth}
          width={isSplit ? panelWidth : 0}
          onResize={commitWidth}
          resizable={isSplit && details.config.resizable}
          position="end"
          className={panelClassName}
          aria-label={isSplit ? detailsAriaLabel : undefined}
        >
          {isSplit ? <panelTeleport.Target /> : null}
        </SplitLayout.Panel>
      </SplitLayout>
      {isMeasuringOverlay && (
        <div hidden aria-hidden="true">
          <panelTeleport.Target />
        </div>
      )}
      {isOverlay && !isMeasuringOverlay && (
        <Overlay
          {...details.domProps}
          ref={details.ref as Ref<HTMLDivElement>}
          minWidth={convertToPx(details.config.minWidth, layout.containerWidth)}
          maxWidth={Math.min(
            details.config.maxWidth !== undefined
              ? convertToPx(details.config.maxWidth, layout.containerWidth)
              : Infinity,
            details.config.resizable
              ? layout.containerWidth - OVERLAY_HANDLE_WIDTH
              : layout.containerWidth,
          )}
          width={panelWidth}
          resizable={details.config.resizable}
          onResizeEnd={commitWidth}
        >
          <panelTeleport.Target />
        </Overlay>
      )}
      {isDrawer && (
        <Drawer
          {...details.domProps}
          ref={details.ref as Ref<HTMLDialogElement>}
          className={clsx(styles.drawer, details.domProps.className)}
          aria-label={detailsAriaLabel}
          isDismissed={layout.collapsed}
          onDismiss={(state) => layout.setCollapsed(state)}
          placement="right"
          width="wide"
        >
          <div className={styles.drawerContent}>
            <panelTeleport.Target />
          </div>
        </Drawer>
      )}
    </DetailsLayoutContext.Provider>
  );
}

/** @internal */
export type LayoutRootProps = {
  details: ExtractedDetailsSlot | null;
  content: ExtractedContentSlot;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'content'>;

/** @internal */
export const DetailsLayout = memo(
  forwardRef<HTMLDivElement, LayoutRootProps>((props, ref) => {
    const { className: rootClassName, details, content, ...rootProps } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useMergeRefs([ref, containerRef]);

    const contentTeleport = useTeleport();
    const panelTeleport = useTeleport();

    return (
      <>
        <contentTeleport.Source>{content.children}</contentTeleport.Source>
        <div
          {...rootProps}
          ref={rootRef}
          className={clsx(styles.layoutRoot, rootClassName)}
        >
          {details ? (
            <DetailsLayoutInner
              details={details}
              content={content}
              containerRef={containerRef}
              contentTeleport={contentTeleport}
              panelTeleport={panelTeleport}
            />
          ) : (
            <main
              ref={content.ref}
              {...content.domProps}
              className={clsx(styles.content, content.domProps.className)}
            >
              <contentTeleport.Target />
            </main>
          )}
        </div>
      </>
    );
  }),
);
DetailsLayout.displayName = 'DetailsLayout (forwardRef)';
